import { Box, Tooltip } from "@mui/material";
import React, { useCallback, useEffect, useRef, useState } from "react";
import PushPinIcon from "@mui/icons-material/PushPin";
import ImpersonationBar from "./ImpersonationBar";
import { HALO_GRADIENT, IMPERSONATION_BAR_HEIGHT_CSS_VAR } from "@features/impersonation-feature/constants";
import { useUnifiedNav } from "@features/unified-side-nav-feature/context/UnifiedNavContext";
import useGetTenantCurrent from "@features/unified-user-header-feature/hooks/useGetTenantCurrent";

const PIN_TAB_COLOR = "warning.main";
const PIN_TAB_HOVER_COLOR = "warning.dark";

export function ImpersonationPin() {
  const [stripHovered, setStripHovered] = useState(false);
  const [barHovered, setBarHovered] = useState(false);
  const {
    impersonationState: { collapsed },
    setImpersonationCollapse,
  } = useUnifiedNav();

  const { isInitialLoading: isInitialLoadingTenant } = useGetTenantCurrent();

  // Measure actual rendered height of the expanded bar so the AppBar push is always exact
  const [expandedBarHeight, setExpandedBarHeight] = useState(65);
  const observerRef = useRef<ResizeObserver | null>(null);

  const dropdownRef = useCallback((el: HTMLDivElement | null) => {
    // Clean up the old observer if it exists
    if (observerRef.current) {
      observerRef.current.disconnect();
      observerRef.current = null;
    }

    if (!el) return;

    const ro = new ResizeObserver(([entry]) => {
      const h = entry.contentRect.height;
      if (h > 0) setExpandedBarHeight(h);
    });
    ro.observe(el);
    observerRef.current = ro;
  }, []);

  const isExpanded = !collapsed && !isInitialLoadingTenant;

  // The collapsed strip is a hover affordance overlay only — it doesn't reserve layout
  // space. Only the pinned/expanded bar pushes the content below it down.
  const topBarOffset = isExpanded ? expandedBarHeight : 0;

  useEffect(() => {
    document.documentElement.style.setProperty(
      IMPERSONATION_BAR_HEIGHT_CSS_VAR,
      `${topBarOffset}px`,
    );
  }, [topBarOffset]);

  const handlePinKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key !== "Enter" && e.key !== " ") {
      return;
    }

    e.preventDefault();
    setImpersonationCollapse(false);
    setStripHovered(false);
  };

  const handleUnpinKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key !== "Enter" && e.key !== " ") {
      return;
    }

    e.preventDefault();
    setImpersonationCollapse(true);
  };

  return (
    <>
      {/* Impersonation expanded bar — fixed at very top of viewport, slides in above AppBar */}
      <Box
        ref={dropdownRef}
        onMouseEnter={() => setBarHovered(true)}
        onMouseLeave={() => setBarHovered(false)}
        aria-hidden={!isExpanded}
        sx={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          zIndex: (t) => t.zIndex.drawer + 20,
          opacity: isExpanded ? 1 : 0,
          pointerEvents: isExpanded ? "auto" : "none",
          transition: "opacity 300ms cubic-bezier(0.4, 0, 0.2, 1)",
        }}
      >
        <ImpersonationBar />
      </Box>

      {/* collapsed: gradient strip + hover zone that reveals the centered "Pin" tab */}
      {collapsed && (
        <Box
          onMouseEnter={() => setStripHovered(true)}
          onMouseLeave={() => setStripHovered(false)}
          sx={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            height: (theme) => theme.spacing(4.25), // gradient strip + tab drop zone
            zIndex: (t) => t.zIndex.drawer + 24,
          }}
        >
          {/* Gradient border */}
          <Box
            sx={{
              height: (theme) => theme.spacing(0.5),
              background: HALO_GRADIENT,
              pointerEvents: "none",
            }}
          />

          {/* Centered orange tab — visible on hover or keyboard focus. */}
          <Tooltip title="Pin impersonation bar" placement="bottom">
            <Box
              role="button"
              tabIndex={0}
              aria-label="Pin impersonation bar"
              aria-controls="impersonation-bar-region"
              aria-expanded={isExpanded}
              onFocus={() => setStripHovered(true)}
              onBlur={() => setStripHovered(false)}
              onKeyDown={handlePinKeyDown}
              onClick={() => {
                setImpersonationCollapse(false);
                setStripHovered(false);
              }}
              sx={{
                position: "absolute",
                left: "50%",
                transform: "translateX(-50%)",
                top: (theme) => theme.spacing(0.5),
                width: (theme) => theme.spacing(5),
                height: (theme) => theme.spacing(3.25),
                bgcolor: PIN_TAB_COLOR,
                borderTopLeftRadius: 0,
                borderTopRightRadius: 0,
                borderBottomLeftRadius: 2,
                borderBottomRightRadius: 2,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                cursor: "pointer",
                opacity: stripHovered ? 1 : 0,
                pointerEvents: stripHovered ? "auto" : "none",
                transition: "opacity 200ms ease",
                "&:hover": { bgcolor: PIN_TAB_HOVER_COLOR },
                "&:focus-visible": {
                  opacity: 1,
                  pointerEvents: "auto",
                  outline: (theme) =>
                    `${theme.spacing(0.25)} solid ${theme.palette.common.white}`,
                  outlineOffset: 2,
                },
              }}
            >
              {/* Rotated pin = "unpinned / available to pin" */}
              <PushPinIcon
                fontSize="small"
                sx={{
                  color: "common.white",
                  transform: "rotate(45deg)",
                }}
              />
            </Box>
          </Tooltip>
        </Box>
      )}

      {/* v2 expanded (pinned): centered orange tab at the bottom of the bar — click to unpin.
          Only visible when the user hovers the bar area or the tab itself. */}
      {!collapsed && (
        <Tooltip title="Unpin impersonation bar" placement="bottom">
          <Box
            role="button"
            tabIndex={0}
            aria-label="Unpin impersonation bar"
            aria-controls="impersonation-bar-region"
            aria-expanded={isExpanded}
            onClick={() => setImpersonationCollapse(true)}
            onMouseEnter={() => setBarHovered(true)}
            onMouseLeave={() => setBarHovered(false)}
            onFocus={() => setBarHovered(true)}
            onBlur={() => setBarHovered(false)}
            onKeyDown={handleUnpinKeyDown}
            sx={{
              position: "fixed",
              left: "50%",
              transform: "translateX(-50%)",
              top: topBarOffset,
              zIndex: (t) => t.zIndex.drawer + 25,
              width: (theme) => theme.spacing(5),
              height: (theme) => theme.spacing(3.25),
              bgcolor: PIN_TAB_COLOR,
              borderTopLeftRadius: 0,
              borderTopRightRadius: 0,
              borderBottomLeftRadius: 2,
              borderBottomRightRadius: 2,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: "pointer",
              opacity: barHovered ? 1 : 0,
              pointerEvents: barHovered ? "auto" : "none",
              transition: `top 300ms cubic-bezier(0.4, 0, 0.2, 1), opacity 200ms ease`,
              "&:hover": { bgcolor: PIN_TAB_HOVER_COLOR },
              "&:focus-visible": {
                opacity: 1,
                pointerEvents: "auto",
                outline: (theme) =>
                  `${theme.spacing(0.25)} solid ${theme.palette.common.white}`,
                outlineOffset: 2,
              },
            }}
          >
            {/* Upright pin = "pinned" */}
            <PushPinIcon fontSize="small" sx={{ color: "common.white" }} />
          </Box>
        </Tooltip>
      )}
    </>
  );
}
